import { useCallback, useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { BookText, Plus, Pencil, Trash2, Star, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PromptEditor } from "./PromptEditor";

export const PromptLibrary = () => {
  const [prompts, setPrompts] = useState(null);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/inquiry-prompts");
      setPrompts(data);
    } catch {
      toast.error("Could not load the prompt library");
      setPrompts([]);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.name}"? Users will no longer see it in their dropdown.`)) return;
    setDeleting(p.id);
    try {
      await api.delete(`/admin/inquiry-prompts/${p.id}`);
      toast.success(`Deleted "${p.name}"`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Delete failed");
    } finally {
      setDeleting(null);
    }
  };

  return (
    <div className="border border-border bg-[#0A0A0A] p-6" data-testid="prompt-library">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground flex items-center gap-2">
            <BookText className="w-3.5 h-3.5 text-[#007AFF]" /> Inquiry drafting prompts
          </p>
          <p className="text-[11px] text-muted-foreground mt-1 max-w-xl">
            System prompts behind "Draft email with AI" on the Inquiries page. Edits go live for every user
            instantly — no redeploy. Users pick one from a dropdown; the default is pre-selected.
          </p>
        </div>
        <Button size="sm" variant="outline" onClick={() => setEditing({})} data-testid="prompt-new-btn"
          className="rounded-sm gap-1.5 h-8 text-xs border-[#007AFF]/50 text-[#007AFF] hover:bg-[#007AFF]/10 hover:text-[#007AFF] shrink-0">
          <Plus className="w-3.5 h-3.5" /> New prompt
        </Button>
      </div>

      {prompts === null ? (
        <p className="text-sm text-muted-foreground font-mono">Loading…</p>
      ) : (
        <div className="space-y-2" data-testid="prompt-list">
          {prompts.map((p) => (
            <div key={p.id} className="border border-border bg-background p-4 flex items-start gap-3" data-testid={`prompt-row-${p.id}`}>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-heading font-bold tracking-tight">{p.name}</p>
                  {p.is_default && (
                    <span className="flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold px-1.5 py-0.5 border border-[#FFCC00]/40 text-[#FFCC00]" data-testid={`prompt-default-${p.id}`}>
                      <Star className="w-2.5 h-2.5" /> Default
                    </span>
                  )}
                  {p.business_types.map((t) => (
                    <span key={t} className="text-[10px] uppercase tracking-widest font-bold px-1.5 py-0.5 border border-[#007AFF]/40 text-[#007AFF]">{t}</span>
                  ))}
                </div>
                {p.description && <p className="text-xs text-muted-foreground mt-1">{p.description}</p>}
                <p className="text-[10px] text-muted-foreground font-mono mt-2 line-clamp-2">{p.prompt}</p>
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                <button onClick={() => setEditing(p)} title="Edit" data-testid={`prompt-edit-${p.id}`}
                  className="w-7 h-7 flex items-center justify-center border border-border text-muted-foreground hover:text-foreground hover:border-[#007AFF]/50 rounded-sm transition-colors duration-200">
                  <Pencil className="w-3.5 h-3.5" />
                </button>
                <button onClick={() => remove(p)} disabled={deleting === p.id} title="Delete" data-testid={`prompt-delete-${p.id}`}
                  className="w-7 h-7 flex items-center justify-center border border-[#FF3B30]/40 text-[#FF3B30] hover:bg-[#FF3B30]/10 rounded-sm transition-colors duration-200">
                  {deleting === p.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing !== null && (
        <PromptEditor prompt={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
      )}
    </div>
  );
};
